import { z } from 'zod'
import { PageQuery } from './pagination.ts'
import { BoolQuery, Uuid, Version, blankAsAbsent, clearable } from './primitives.ts'

/**
 * ══ HỢP ĐỒNG API — MẪU IN (F11) ═══════════════════════════════════════════════════════════════
 * Nguồn sự thật duy nhất cho `/api/v1/az/doc-templates`.
 *
 * ⚠ `body` là VĂN BẢN CÓ THẺ, cố ý không lọc — xem lý do ở `@rs/contract/app/doc-template.ts`.
 *   Trần 200 000 ký tự: mẫu hợp đồng dài nhất của bản cũ (`azContractTemplates.ts`) khoảng 18 000
 *   ký tự, nên trần này rộng gấp mười lần mà vẫn chặn được việc nhét cả một tệp vào cột `TEXT`.
 */

export const DocTemplateKind = z.enum(['contract', 'appendix', 'quote', 'waybill', 'handover', 'bill'])
export const TemplatePartyKind = z.enum(['customer', 'vendor'])

const BODY_MAX = 200_000
/** Link Drive dài nhất thực tế khoảng 120 ký tự; 1 000 là rộng rãi mà vẫn chặn được việc dán nhầm
 *  cả một tài liệu vào ô link. Bản cũ không giới hạn vì nó ghi thẳng vào kho trình duyệt. */
const DRIVE_URL_MAX = 1_000

export const CreateDocTemplateBody = z.object({
  kind: DocTemplateKind,
  partyKind: TemplatePartyKind.optional(),
  name: z.string().min(1, 'Tên mẫu không được để trống.').max(200),
  body: z.string().min(1, 'Nội dung mẫu không được để trống.').max(BODY_MAX),
  driveUrl: blankAsAbsent(z.string().max(DRIVE_URL_MAX)),
  isDefault: z.boolean().optional(),
  sortOrder: blankAsAbsent(z.coerce.number().int()),
})

/**
 * ⭐ THAY MẪU CŨ NHẤT (T25, rà 16/10) — đủ 5 mẫu thì mẫu mới THAY mẫu `:id` (bản cũ `addNew`, `AzContract.tsx:1932-1941`).
 *   Một cửa, một giao dịch: xoá rồi tạo ở máy chủ. Trước đây trình duyệt gọi xoá RỒI mới tạo ⇒ tạo lỗi là mất mẫu cũ.
 *   `version` là khoá lạc quan của mẫu BỊ THAY; phần còn lại y hệt thân tạo mẫu.
 */
export const ReplaceDocTemplateBody = CreateDocTemplateBody.extend({ version: Version })

/** Sửa mẫu là gửi BẢN VÁ — màn chỉ gửi ô vừa đổi (cùng lý lẽ với `UpdateQuoteBody`). */
export const UpdateDocTemplateBody = z.object({
  version: Version,
  name: blankAsAbsent(z.string().max(200)),
  body: blankAsAbsent(z.string().max(BODY_MAX)),
  /** `clearable`, KHÔNG phải `blankAsAbsent`: xoá trắng ô link là muốn GỠ link, không phải "giữ
   *  nguyên" (xem `primitives.ts` — đây đúng ca mà hai primitive đó khác nhau). */
  driveUrl: clearable(z.string().max(DRIVE_URL_MAX)),
  isDefault: z.boolean().optional(),
  sortOrder: blankAsAbsent(z.coerce.number().int()),
  active: z.boolean().optional(),
})

/** Xoá đi bằng POST `/:id/delete` kèm `version` — cùng khuôn `ContractStepBody`. Cửa `DELETE` của
 *  HTTP không mang thân request theo cách mọi máy chủ trung gian đều tôn trọng, mà xoá cũng là một
 *  lần ghi nên vẫn phải va được khoá lạc quan (A4). */
export const DocTemplateStepBody = z.object({ version: Version })

export const ListDocTemplateQuery = PageQuery.extend({
  kind: DocTemplateKind.optional(),
  partyKind: TemplatePartyKind.optional(),
  activeOnly: blankAsAbsent(BoolQuery),
})

export const DocTemplateIdParams = z.object({ id: Uuid })

export type ListDocTemplateQueryT = z.infer<typeof ListDocTemplateQuery>
export type CreateDocTemplateBodyT = z.infer<typeof CreateDocTemplateBody>
export type UpdateDocTemplateBodyT = z.infer<typeof UpdateDocTemplateBody>
export type ReplaceDocTemplateBodyT = z.infer<typeof ReplaceDocTemplateBody>
